// A Supabase password-recovery link establishes a SESSION on arrival. That is
// how the flow is designed to work — but it means a recovery link that lands
// anywhere other than /reset-password gets silently swallowed by our own auth
// guards, because by the time they run the visitor looks like a normal
// signed-in operator.
//
// UAT, 2026-08-11 (Juan): clicked "Forgot password?", got the email, clicked
// through — and landed authenticated on rockwell.uat.digavouch.com/dashboard
// with no way to set a password. No error, anywhere. He was simply logged in.
//
// Two routes swallow it, both by design for every OTHER visitor:
//   App.tsx    `/`      -> <ProtectedRoute><OperatorHomeRedirect /></…>
//   Login.tsx  `/login` -> if (user) <Navigate to={from} /> -> `/`
//
// The trigger was a GoTrue redirect-allowlist gap: `redirectTo` pointed at
// <tenant>.uat.digavouch.com/reset-password, that origin was not on the
// allowlist, so GoTrue fell back to the project's Site URL and the /reset-password
// PATH was lost. Fixing the allowlist fixes that instance. It does not fix the
// class: any future Site-URL change, new tenant subdomain, or forgotten
// allowlist entry silently reintroduces "reset link logs you in instead."
//
// So we detect the recovery landing ourselves and route to /reset-password
// regardless of which path GoTrue dropped us on.
//
// WHY A MODULE-LOAD CAPTURE AND NOT JUST THE AUTH EVENT: supabase-js consumes
// the URL fragment and CLEARS it (detectSessionInUrl) as part of an async
// init. Anything that reads window.location.hash from a React effect is racing
// that cleanup. Reading it synchronously at module load — before React renders
// — cannot lose. The PASSWORD_RECOVERY event is kept as a second, independent
// trigger (see useRecoveryLanding) because it also covers flows where the
// marker never appears in the fragment we see.

/**
 * True when a URL fragment is a Supabase password-recovery deep link.
 *
 * The fragment GoTrue appends looks like:
 *   #access_token=…&refresh_token=…&token_type=bearer&type=recovery
 *
 * `type=recovery` is the marker. It rides along on the Site-URL fallback too,
 * which is precisely what makes it usable as a signal here: the path can be
 * wrong, but the fragment still says what this link was for.
 */
export function isRecoveryFragment(hash: string): boolean {
  const fragment = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!fragment) return false;
  // A fragment can legitimately be a plain anchor (#section), which
  // URLSearchParams parses to a single valueless key — get() returns null, so
  // this stays false without a special case.
  return new URLSearchParams(fragment).get("type") === "recovery";
}

let landed = false;

export interface RecoveryTokens {
  access_token: string;
  refresh_token: string;
  /** The project that minted them, off the token's `iss`. Null if undecodable. */
  issuer: string | null;
}

let tokens: RecoveryTokens | null = null;

/** Decode a JWT payload's `iss` without verifying anything. Null on any failure. */
function issuerOf(accessToken: string): string | null {
  try {
    const body = accessToken.split(".")[1];
    if (!body) return null;
    const padded = body.replace(/-/g, "+").replace(/_/g, "/");
    const claims = JSON.parse(atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, "=")));
    return typeof claims?.iss === "string" && claims.iss ? claims.iss : null;
  } catch {
    return null;
  }
}

/**
 * The issuer a project URL mints tokens under.
 *
 * ⚠️ THE ONE COPY, on purpose. Two decisions depend on this string agreeing
 * with itself: whether to take the fragment out of the URL (here) and whether
 * to claim the tokens afterwards (the auth providers module). If those two
 * derived it separately and either drifted — a path prefix, a different
 * trailing-slash rule — the fragment would be stripped and then never claimed,
 * and the operator would get a set-a-password screen that never becomes ready
 * rather than any kind of error. Import it; do not re-derive it.
 */
export function issuerForProjectUrl(url: string | undefined): string | null {
  const trimmed = (url ?? "").replace(/\/+$/, "");
  return trimmed ? `${trimmed}/auth/v1` : null;
}

/**
 * Record whether this page load is a recovery landing. Call once, as early as
 * possible — see the module comment for why the timing matters.
 *
 * Idempotent and additive: a second call cannot clear a capture the first one
 * made, so import order can never silently disarm this.
 */
export function captureRecoveryLanding(
  hash: string = typeof window === "undefined" ? "" : window.location.hash,
): void {
  if (!isRecoveryFragment(hash)) return;

  // The marker FIRST, and unconditionally. It is what gets the operator to the
  // set-a-password screen, and it must survive every failure below — an
  // unroutable link that silently logs them in instead is the exact incident
  // this module exists to prevent.
  landed = true;

  const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const access_token = params.get("access_token");
  const refresh_token = params.get("refresh_token");
  if (!access_token || !refresh_token) return;

  const issuer = issuerOf(access_token);
  tokens = { access_token, refresh_token, issuer };

  // ---------------------------------------------------------------------
  // Who is allowed to consume this fragment.
  //
  // Nothing in the fragment's PARAMETERS says which project minted these
  // tokens, but the access token is a JWT and carries `iss`, so the fragment
  // does say — just not where a reader would look. That matters once more than
  // one auth client exists: the one with URL detection on consumes whatever it
  // finds and clears it for the other, and supabase-js does not verify
  // signatures in the browser, so it will happily store a foreign project's
  // tokens under its own key. updateUser then posts them to the wrong API,
  // which rejects them, and the fragment is gone so a reload cannot recover.
  //
  // ⚠️ We intervene ONLY when the tokens are not the original client's. Its
  // path is proven, had a production incident behind it, and its module-load
  // timing is load-bearing — leaving it untouched is the whole point.
  //
  // ⚠️ Unroutable (no `iss`, not a JWT, unknown issuer) also falls through to
  // that path. Handling it the old way is no worse than before this existed;
  // guessing would be.
  // ---------------------------------------------------------------------
  const original = issuerForProjectUrl(import.meta.env.VITE_SUPABASE_URL);
  if (!issuer || !original || issuer.replace(/\/+$/, "") === original) return;

  // Take it out of the URL before supabase-js's async init can reach it. That
  // ordering is the same guarantee main.tsx already relies on: this statement
  // is synchronous, that work is queued.
  if (typeof history !== "undefined" && typeof history.replaceState === "function") {
    const { pathname, search } = typeof window === "undefined" ? { pathname: "", search: "" } : window.location;
    history.replaceState(null, "", `${pathname}${search}`);
  }
}

/** The captured tokens, or null. Non-destructive, for the same reason as {@link peekRecoveryLanding}. */
export function peekRecoveryTokens(): RecoveryTokens | null {
  return tokens;
}

/**
 * Take the captured tokens, clearing them so they cannot be applied twice.
 *
 * ⚠️ Separate from {@link clearRecoveryLanding} on purpose. That one releases
 * the NAVIGATION MARKER and is called from a mount effect the moment the marker
 * has done its job; if it also dropped the tokens it would destroy them before
 * anything had a chance to apply them, and since the fragment is already out of
 * the URL by then, a reload could not recover. Only the code that actually
 * establishes the session may consume these.
 */
export function consumeRecoveryTokens(): RecoveryTokens | null {
  const held = tokens;
  tokens = null;
  return held;
}

/**
 * Read the captured flag WITHOUT clearing it.
 *
 * Deliberately non-destructive: the reader is a lazy `useState` initializer,
 * and React StrictMode double-invokes those in development. A destructive read
 * would return true on the first invocation and false on the second — silently
 * disabling this in dev the day someone wraps the app in <StrictMode>. Clearing
 * is a separate, idempotent step (see {@link clearRecoveryLanding}).
 */
export function peekRecoveryLanding(): boolean {
  return landed;
}

/**
 * Drop the captured navigation flag. Idempotent — safe under StrictMode's
 * double effects.
 *
 * ⚠️ Deliberately does NOT touch the captured tokens — see
 * {@link consumeRecoveryTokens}.
 */
export function clearRecoveryLanding(): void {
  landed = false;
}

/** Test-only — drop the flag AND the tokens, for suites that reset between cases. */
export function _resetRecoveryLandingForTest(): void {
  landed = false;
  tokens = null;
}
